import { parseFontWithCalc, getFontWithoutColor, fixFontFamily, replaceSpxWithPx } from '@wix/thunderbolt-becky-root'
import { consts, generateStylableTheme } from '@wix/stylable-viewer-flatten'
import type { FetchFn } from '@wix/thunderbolt-symbols'
import type { BeckyRequestPayload } from '@wix/thunderbolt-becky-root'
import type { MetricsReporter, ThemeData, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import type { Cache } from '@wix/thunderbolt-ssr-api'
import type { PanelComponentsMetadata } from '@wix/stylable-panel-drivers'
import { StylableEditor } from '@wix/stylable-panel-drivers'
import {
	addMetadataSuffixToUrl,
	getEditorElementsAssetUrl,
	mergeStylableComponentsMetadata,
	registerStylableExtensions,
} from '@wix/thunderbolt-commons'
import { getParsedFontDeclarations } from '@wix/stylable-viewer-flatten/dist/fonts/font-utils'

const fetchMetaData = async (
	stylableMetaData: string,
	fetch: FetchFn,
	fileRepoUrl: string,
	metricsReporter: MetricsReporter,
	cache: Cache<string, PanelComponentsMetadata>
): Promise<PanelComponentsMetadata> => {
	const url = addMetadataSuffixToUrl(stylableMetaData)
	const cachedMetadata = cache.get(url)
	if (cachedMetadata) {
		return cachedMetadata
	}

	return metricsReporter.runAsyncAndReport(async () => {
		const metadata = await fetch(getEditorElementsAssetUrl(url, fileRepoUrl)).then((x) => x.json())

		// TODO - this should be removed once Yoshi is updated with correct build in editor-elements
		if (!metadata.packages['wix-ui-santa']) {
			metadata.packages['wix-ui-santa'] = '/editor-elements'
		}

		cache.set(url, metadata)
		return metadata as PanelComponentsMetadata
	}, 'stylableFetch')
}

const getStylableUrls = (stylableMetadataURLs: string) => {
	return stylableMetadataURLs ? JSON.parse(stylableMetadataURLs) : []
}

async function getStylableMetadata({
	fileRepoUrl,
	urls,
	cache,
	metricsReporter,
	fetch,
}: {
	fileRepoUrl: string
	urls: Array<string>
	metricsReporter: MetricsReporter
	fetch: FetchFn
	cache: Cache<string, PanelComponentsMetadata>
}): Promise<PanelComponentsMetadata> {
	const multiUrlKey = urls.join('_')

	const cachedMetadata = cache.get(multiUrlKey)
	if (cachedMetadata) {
		return cachedMetadata
	}

	const pendingStylable = urls.map(async (stylableMetadataURL) =>
		fetchMetaData(stylableMetadataURL, fetch, fileRepoUrl, metricsReporter, cache)
	)

	const stylableCSSResults = await Promise.all(pendingStylable)
	const stylableMetadata = mergeStylableComponentsMetadata(stylableCSSResults)

	cache.set(multiUrlKey, stylableMetadata)

	return stylableMetadata
}

const updateStylableTheme = (
	stylableEditor: StylableEditor,
	themeData: ThemeData,
	path: string,
	themeFontsSpxResolved: ThemeFontMap | undefined
) => {
	const { stylableDriver } = stylableEditor
	const getFontWithFallbacks = (fontFamiliesArray: Array<string>) =>
		fixFontFamily(
			fontFamiliesArray
				.map((font) => getFontWithoutColor(font))
				.filter((v) => v)
				.join(',')
		)
	// if undefined, stylable-viewer-flatten will extract and process the fonts from theme data
	const fontThemes = themeFontsSpxResolved
		? Object.fromEntries(
			Object.entries(themeFontsSpxResolved!).map(([key, value]) => {
				const parsedFont = parseFontWithCalc(value)
				const fontSizeUnit = replaceSpxWithPx(parsedFont.sizeUnit)
				parsedFont.size = `${parsedFont.size}${fontSizeUnit}`
				const fontWithFallbacks = getFontWithFallbacks(parsedFont.family)
				return [
					key,
					getParsedFontDeclarations({
						...parsedFont,
						fontWithFallbacks,
					}),
				]
			})
		)
		: undefined

	const globalCssVariables = ['theme-spx-ratio']
	const theme = generateStylableTheme({
		themeData,
		fontThemes,
		globalCssVariables,
	})
	stylableDriver.writeFile(path, theme)
	stylableDriver.reloadSiteColors()
}

export type GetStylableInstanceParams = {
	payload: BeckyRequestPayload
	fetch: FetchFn
	fileRepoUrl: string
	metricsReporter: MetricsReporter
	cache: Cache<string, PanelComponentsMetadata>
	themeData: ThemeData
	themeFontsSpxResolved: ThemeFontMap
	staticMediaUrl: string
}

export const getStylableInstance = async ({
	payload,
	fetch,
	fileRepoUrl,
	metricsReporter,
	cache,
	themeData,
	themeFontsSpxResolved,
	staticMediaUrl,
}: GetStylableInstanceParams): Promise<StylableEditor | undefined> => {
	const { stylableMetadataURLs } = payload
	const urls = getStylableUrls(stylableMetadataURLs)

	if (!urls.length) {
		return
	}

	const stylableMetadata = await getStylableMetadata({
		urls,
		cache,
		metricsReporter,
		fileRepoUrl,
		fetch,
	})

	const stylableInstance = new StylableEditor(stylableMetadata, consts.SITE_ST_CSS_PATH, {
		newElementree: true,
	})
	registerStylableExtensions(stylableInstance, staticMediaUrl)

	updateStylableTheme(stylableInstance, themeData, consts.SITE_ST_CSS_PATH, themeFontsSpxResolved)
	return stylableInstance
}
